import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { getIesoPricingData } from '../utils/chartAPI';

export default function useLMPORData({
  site,
  msSessionInfo,
  startDate,
  endDate,
  inView,
  refreshKey,
}) {
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const hasFetched = useRef(false);

  useEffect(() => {
    hasFetched.current = false;
  }, [refreshKey, startDate?.valueOf(), endDate?.valueOf()]);

  useEffect(() => {
    if (!inView || !site?.id || !msSessionInfo) return;
    if (refreshKey === undefined && hasFetched.current) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const start = dayjs(startDate || dayjs().startOf('day'));
        let end = dayjs(endDate || start.add(1, 'day').subtract(1, 'minute'));
        if (!end.isAfter(start)) end = start.add(1, 'minute');

        const [realTimeRaw, dayAheadRaw] = await Promise.all([
          getIesoPricingData(msSessionInfo, site, start, end, 'real_time', '10S'),
          getIesoPricingData(msSessionInfo, site, start, end, 'day_ahead', '10S'),
        ]);

        const toMinuteTs = (t) => dayjs(t).second(0).millisecond(0).valueOf();

        const rtMap = {};
        (realTimeRaw?.prices ?? []).forEach((p) => {
          rtMap[toMinuteTs(p.time)] = p.price;
        });

        const daMap = {};
        (dayAheadRaw?.prices ?? []).forEach((p) => {
          daMap[toMinuteTs(p.time)] = p.price;
        });

        const allTs = Array.from(
          new Set([...Object.keys(rtMap), ...Object.keys(daMap)].map(Number))
        ).sort((a, b) => a - b);

        const rtAligned = allTs.map((ts) => ({ x: ts, y: rtMap[ts] ?? null }));
        const daAligned = allTs.map((ts) => ({ x: ts, y: daMap[ts] ?? null }));

        setSeries([
          { name: 'Day Ahead', type: 'line', data: daAligned },
          { name: 'Real Time', type: 'line', data: rtAligned },
        ]);

        hasFetched.current = true;
      } catch (err) {
        console.error('LMP OR fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [inView, site?.id, msSessionInfo, startDate?.valueOf(), endDate?.valueOf(), refreshKey]);

  return { loading, series };
}
